import React, { useState, useMemo, useRef } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Input } from '@/components/ui/input';
import { Braces, Search, ChevronRight, Lock } from 'lucide-react';
import type { ActionTemplate, WorkflowNode, WorkflowEdge, OutputField, CustomBlock } from '../../api/client';

export interface VariableGroup {
  label: string;
  description?: string;
  variables: OutputField[];
}

function getUpstreamNodeIds(nodeId: string, edges: WorkflowEdge[]): string[] {
  const visited = new Set<string>();
  const queue = [nodeId];
  while (queue.length > 0) {
    const current = queue.shift()!;
    for (const edge of edges) {
      if (edge.target === current && !visited.has(edge.source)) {
        visited.add(edge.source);
        queue.push(edge.source);
      }
    }
  }
  return Array.from(visited);
}

// Maps action output keys to top-level context aliases that the engine
// promotes via mergeOutputIntoContext. Only shown when a producing step
// is actually upstream.
const PROMOTED_ALIASES: Record<string, { alias: string; label: string; description: string }[]> = {
  'pr_url':              [{ alias: 'pr_url', label: 'PR URL', description: 'Shorthand — promoted from the upstream Create PR step.' }],
  'pagerduty_dedup_key': [{ alias: 'pagerduty_dedup_key', label: 'PD Dedup Key', description: 'Shorthand — promoted from the upstream PagerDuty step.' }],
  'pagerduty_url':       [{ alias: 'pagerduty_url', label: 'PagerDuty URL', description: 'Shorthand — promoted from the upstream PagerDuty step.' }],
  'slack_channel_id':    [{ alias: 'slack_channel_id', label: 'Slack Channel ID', description: 'Shorthand — promoted from the upstream Slack message step.' }],
  'slack_thread_ts':     [{ alias: 'slack_thread_ts', label: 'Slack Thread TS', description: 'Shorthand — promoted from the upstream Slack message step.' }],
};

export function buildVariableGroups(
  currentNodeId: string,
  allNodes: WorkflowNode[],
  edges: WorkflowEdge[],
  actionTemplates: ActionTemplate[],
  triggerVariables: Record<string, OutputField[]>,
  customBlocks?: CustomBlock[],
): VariableGroup[] {
  const groups: VariableGroup[] = [];

  // For Each nodes get an item-scoped group: {{item}} / {{item.<field>}} /
  // {{item_index}} only resolve inside the fan-out node's own config.
  const currentNode = allNodes.find(n => n.id === currentNodeId);
  if (currentNode?.type === 'for_each') {
    groups.push({
      label: 'Current Item',
      description: 'Available only inside this For Each block',
      variables: [
        { name: 'item', label: 'Item', description: 'The current list element (JSON when it is an object).' },
        { name: 'item.<field>', label: 'Item Field', description: 'A field of the current element, e.g. {{item.title}}.' },
        { name: 'item_index', label: 'Item Index', description: 'Zero-based index of the current element.' },
      ],
    });
  }

  const triggerNode = allNodes.find(n => n.type === 'trigger');
  const triggerSource = triggerNode?.data?.source || 'kubernetes';
  const triggerVars = triggerVariables[triggerSource] || [];

  const enrichedTriggerVars = [...triggerVars];
  if (triggerSource === 'custom_webhook' && triggerNode?.data?.custom_block_id && customBlocks) {
    const block = customBlocks.find(b => b.id === triggerNode.data.custom_block_id);
    if (block?.payload_mapping) {
      for (const key of Object.keys(block.payload_mapping)) {
        enrichedTriggerVars.push({
          name: `signal.${key}`,
          label: key,
          description: `Mapped from webhook payload: ${block.payload_mapping[key]}`,
        });
      }
    }
  }

  // Scan all nodes for custom {{request.*}} variables used in the workflow
  if (triggerSource === 'request') {
    const existingVarNames = new Set(enrichedTriggerVars.map(v => v.name));
    const requestVarRegex = /\{\{request\.([a-zA-Z0-9_]+)\}\}/g;
    for (const node of allNodes) {
      const dataStr = JSON.stringify(node.data || {});
      let match;
      while ((match = requestVarRegex.exec(dataStr)) !== null) {
        const varName = `request.${match[1]}`;
        if (!existingVarNames.has(varName)) {
          existingVarNames.add(varName);
          enrichedTriggerVars.push({
            name: varName,
            label: match[1].replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
            description: `Custom parameter extracted from the user's request.`,
          });
        }
      }
    }
  }

  if (enrichedTriggerVars.length > 0) {
    groups.push({
      label: 'Trigger',
      description: `Variables from the ${triggerSource} trigger`,
      variables: enrichedTriggerVars,
    });
  }

  const upstreamIds = getUpstreamNodeIds(currentNodeId, edges);
  const templateMap = new Map(actionTemplates.map(a => [a.id, a]));

  const promotedAliases = new Map<string, OutputField>();

  for (const uid of upstreamIds) {
    const upstreamNode = allNodes.find(n => n.id === uid);
    if (!upstreamNode || (upstreamNode.type !== 'action' && upstreamNode.type !== 'approval')) continue;

    const actionId = upstreamNode.data?.action;
    const tmpl = actionId ? templateMap.get(actionId) : undefined;

    // Static outputs declared on the action template.
    const baseOutputs: OutputField[] = tmpl?.outputs ? [...tmpl.outputs] : [];

    // Dynamic outputs inferred for an Execute Script node (the user analyzed the
    // script). These are nested under the block's "outputs" object, so they are
    // referenced as step_outputs.<id>.outputs.<key>.
    const scriptOutputs: { key: string; label?: string; description?: string }[] = Array.isArray(upstreamNode.data?.script_outputs)
      ? upstreamNode.data.script_outputs
      : [];

    if (!baseOutputs.length && !scriptOutputs.length) continue;

    const nodeLabel = upstreamNode.data?.label || tmpl?.name || actionId || uid;
    const prefixedOutputs: OutputField[] = baseOutputs.map(o => ({
      name: `step_outputs.${uid}.${o.name}`,
      label: o.label,
      description: o.description,
      sensitive: o.sensitive,
    }));
    for (const so of scriptOutputs) {
      if (!so?.key) continue;
      prefixedOutputs.push({
        name: `step_outputs.${uid}.outputs.${so.key}`,
        label: so.label || so.key,
        description: so.description || 'Script output (from /workspace/outputs.json).',
      });
    }

    groups.push({
      label: nodeLabel,
      description: `Step ID: ${uid}`,
      variables: prefixedOutputs,
    });

    for (const o of baseOutputs) {
      const aliases = PROMOTED_ALIASES[o.name];
      if (aliases) {
        for (const a of aliases) {
          if (!promotedAliases.has(a.alias)) {
            promotedAliases.set(a.alias, { name: a.alias, label: a.label, description: a.description });
          }
        }
      }
    }
  }

  if (promotedAliases.size > 0) {
    groups.push({
      label: 'Promoted Shortcuts',
      description: 'Top-level aliases auto-set by upstream steps',
      variables: Array.from(promotedAliases.values()),
    });
  }

  return groups;
}

interface VariablePickerProps {
  variableGroups: VariableGroup[];
  onInsert: (variable: string) => void;
}

export function VariablePicker({ variableGroups, onInsert }: VariablePickerProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const searchRef = useRef<HTMLInputElement>(null);

  const filteredGroups = useMemo(() => {
    if (!search.trim()) return variableGroups;
    const q = search.toLowerCase();
    return variableGroups
      .map(g => ({
        ...g,
        variables: g.variables.filter(
          v => v.name.toLowerCase().includes(q) || v.label.toLowerCase().includes(q)
        ),
      }))
      .filter(g => g.variables.length > 0);
  }, [variableGroups, search]);

  const allExpanded = search.trim().length > 0;

  const toggleGroup = (label: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });
  };

  const handleInsert = (varName: string) => {
    onInsert(`{{${varName}}}`);
    setOpen(false);
    setSearch('');
  };

  const totalVars = variableGroups.reduce((sum, g) => sum + g.variables.length, 0);
  if (totalVars === 0) return null;

  return (
    <Popover open={open} onOpenChange={(v) => { setOpen(v); if (v) { setSearch(''); setExpandedGroups(new Set()); } }}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors px-1.5 py-0.5 rounded hover:bg-accent"
          title="Insert template variable"
        >
          <Braces className="h-3 w-3" />
          <span>Variables</span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-80 p-0"
        align="start"
        side="bottom"
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          setTimeout(() => searchRef.current?.focus(), 0);
        }}
      >
        <div className="p-2 border-b">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              ref={searchRef}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search variables..."
              className="h-7 pl-7 text-xs"
            />
          </div>
        </div>
        <div className="overflow-y-auto" style={{ maxHeight: 'min(320px, var(--radix-popover-content-available-height, 320px) - 44px)' }}>
          <div className="p-1">
            {filteredGroups.length === 0 && (
              <p className="text-xs text-muted-foreground px-2 py-3 text-center">No matching variables</p>
            )}
            {filteredGroups.map((group) => {
              const isExpanded = allExpanded || expandedGroups.has(group.label);
              return (
                <div key={group.label + (group.description || '')} className="mb-0.5">
                  <button
                    type="button"
                    onClick={() => toggleGroup(group.label)}
                    className="flex items-center gap-1 w-full text-left px-2 py-1.5 text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-accent rounded transition-colors"
                  >
                    <ChevronRight className={`h-3 w-3 transition-transform shrink-0 ${isExpanded ? 'rotate-90' : ''}`} />
                    <span className="truncate">{group.label}</span>
                    {group.description && (
                      <span className="text-[10px] opacity-50 truncate">{group.description}</span>
                    )}
                    <span className="ml-auto text-[10px] opacity-60 shrink-0">{group.variables.length}</span>
                  </button>
                  {isExpanded && (
                    <div className="ml-2 border-l pl-1.5">
                      {group.variables.map((v) => (
                        <button
                          key={v.name}
                          type="button"
                          onClick={() => handleInsert(v.name)}
                          className="flex flex-col w-full text-left px-2 py-1.5 rounded hover:bg-accent transition-colors group"
                        >
                          <code className="text-[11px] font-mono text-foreground group-hover:text-foreground break-all">
                            {`{{${v.name}}}`}
                            {v.sensitive && (
                              <span className="ml-1 inline-flex items-center gap-0.5 text-[9px] font-sans text-amber-600 dark:text-amber-400 align-middle" title="Secret value: usable in downstream steps but redacted from logs and step results">
                                <Lock className="h-2.5 w-2.5" />secret
                              </span>
                            )}
                          </code>
                          {v.description && (
                            <span className="text-[10px] text-muted-foreground mt-0.5 leading-tight">{v.description}</span>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
